import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Trash2, Edit2 } from 'lucide-react';
import { lampshadeVariants, cascadeContainerVariants, cascadeItemVariants } from '../animations/LampshadeAnimations';
import { useLampshadeGestures } from '../../hooks/useLampshadeGestures';
import { supabase } from '../../lib/supabase';
import { MountainCardPeaks } from './MountainCardPeaks';
import { ResetPeriodDisplay } from './ResetPeriodDisplay';
import { ResetPeriodBottomSheet } from './ResetPeriodBottomSheet';
const accentColors = [
    'from-purple-500 to-pink-500',
    'from-blue-500 to-cyan-500',
    'from-emerald-500 to-teal-500',
    'from-orange-500 to-red-500',
    'from-indigo-500 to-purple-500',
    'from-rose-500 to-pink-500',
];
export function RangeCardPeaks({ range, userId, resetPeriod, onUpdate, onDelete, onSaveResetPeriod }) {
    const [isEditing, setIsEditing] = useState(false);
    const [editName, setEditName] = useState(range.name);
    const [showResetForm, setShowResetForm] = useState(false);
    const [isSavingReset, setIsSavingReset] = useState(false);
    const { isCollapsed, handlers, hapticFeedback } = useLampshadeGestures({
        onSwipeUp: () => handleCollapse(true),
        onSwipeDown: () => handleCollapse(false),
    });
    const colorIndex = range.id.charCodeAt(0) % accentColors.length;
    const accentColor = accentColors[colorIndex];
    const mountainCount = range.mountains?.length || 0;
    const completedMountains = range.mountains?.filter((m) => m.completed).length || 0;
    const completionPercent = mountainCount > 0 ? Math.round((completedMountains / mountainCount) * 100) : 0;
    const handleCollapse = (collapse) => {
        hapticFeedback(collapse ? 'medium' : 'light');
    };
    const handleSaveName = async () => {
        if (editName === range.name) {
            setIsEditing(false);
            return;
        }
        try {
            await supabase
                .from('ranges')
                .update({ name: editName })
                .eq('id', range.id)
                .eq('user_id', userId);
            onUpdate({ ...range, name: editName });
            setIsEditing(false);
            hapticFeedback('light');
        }
        catch (err) {
            console.error('Error updating range:', err);
            setEditName(range.name);
        }
    };
    const handleDelete = async () => {
        if (!confirm(`Delete "${range.name}"? This cannot be undone.`))
            return;
        try {
            await supabase.from('ranges').delete().eq('id', range.id).eq('user_id', userId);
            onDelete(range.id);
            hapticFeedback('heavy');
        }
        catch (err) {
            console.error('Error deleting range:', err);
        }
    };
    const handleSaveResetPeriod = async (period) => {
        setIsSavingReset(true);
        try {
            await onSaveResetPeriod(range.id, period);
            setShowResetForm(false);
            hapticFeedback('light');
        }
        finally {
            setIsSavingReset(false);
        }
    };
    return (_jsxs(motion.div, { className: "w-full", initial: "visible", animate: isCollapsed ? 'collapsed' : 'visible', transition: { duration: 0.3 }, children: [_jsxs("div", { className: "rounded-2xl overflow-hidden shadow-lg backdrop-blur-xl border", style: {
                    backgroundColor: 'rgba(15, 23, 42, 0.7)',
                    borderColor: 'rgba(14, 165, 233, 0.2)',
                }, children: [_jsxs("div", { className: `bg-gradient-to-r ${accentColor} p-4 cursor-pointer transition-all`, ...handlers, onClick: () => handlers.onClick(), children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { className: "flex-1 min-w-0", children: [isEditing ? (_jsx("input", { autoFocus: true, value: editName, onChange: (e) => setEditName(e.target.value), onBlur: handleSaveName, onKeyDown: (e) => e.key === 'Enter' && handleSaveName(), className: "bg-white/20 text-white placeholder-white/60 rounded px-2 py-1 w-full backdrop-blur text-lg font-bold", placeholder: "Range name", onClick: (e) => e.stopPropagation() })) : (_jsx("h3", { className: "text-white font-bold text-lg truncate", children: range.name })), _jsxs("p", { className: "text-white/70 text-sm mt-1", children: [completedMountains, "/", mountainCount, " mountains \u2022 ", completionPercent, "%"] })] }), _jsx("div", { className: "flex items-center gap-2 ml-3 flex-shrink-0", children: _jsx(motion.div, { animate: { rotate: isCollapsed ? -180 : 0 }, transition: { duration: 0.3 }, children: _jsx(ChevronDown, { className: "w-5 h-5 text-white" }) }) })] }), _jsx("div", { className: "mt-3 h-1 bg-white/20 rounded-full overflow-hidden", children: _jsx(motion.div, { className: "h-full bg-white rounded-full", initial: { width: 0 }, animate: { width: `${completionPercent}%` }, transition: { type: 'spring', stiffness: 100, damping: 15 } }) })] }), !isCollapsed && (_jsxs(motion.div, { variants: lampshadeVariants, initial: "visible", animate: "visible", exit: "collapsed", className: "p-4 space-y-2", children: [mountainCount === 0 ? (_jsx("div", { className: "text-center py-6", style: { color: 'var(--text-secondary)' }, children: _jsx("p", { className: "text-sm", children: "No mountains yet. Add one to get started!" }) })) : (_jsx(motion.div, { variants: cascadeContainerVariants, initial: "visible", animate: "visible", className: "space-y-2", children: range.mountains?.map((mountain, idx) => (_jsx(motion.div, { variants: cascadeItemVariants, children: _jsx(MountainCardPeaks, { mountain: mountain, rangeId: range.id, userId: userId }) }, mountain.id))) })), _jsx("div", { className: "mt-4 pt-4 border-t", style: { borderColor: 'var(--border-color)' }, children: _jsx(ResetPeriodDisplay, { period: resetPeriod, onEdit: () => setShowResetForm(true) }) }), _jsxs("div", { className: "flex gap-2 mt-4 pt-4 border-t", style: { borderColor: 'var(--border-color)' }, children: [_jsxs("button", { onClick: (e) => {
                                            e.stopPropagation();
                                            setIsEditing(!isEditing);
                                            hapticFeedback('light');
                                        }, className: "flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2", style: {
                                            backgroundColor: 'var(--bg-base)',
                                            color: 'var(--text-secondary)',
                                        }, children: [_jsx(Edit2, { className: "w-4 h-4" }), "Edit"] }), _jsxs("button", { onClick: (e) => {
                                            e.stopPropagation();
                                            handleDelete();
                                        }, className: "flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 hover:bg-red-500/20 hover:text-red-400", style: { color: 'var(--text-secondary)' }, children: [_jsx(Trash2, { className: "w-4 h-4" }), "Delete"] })] })] }))] }), _jsx(ResetPeriodBottomSheet, { isOpen: showResetForm, period: resetPeriod, onClose: () => setShowResetForm(false), onSave: handleSaveResetPeriod, isSaving: isSavingReset })] }));
}
